import { LDateTime } from '@app/components/date/LDateTime';
import { LTime } from '@app/components/date/LTime';
import { AuditMatch, Seconds } from '@mi-front/core';
import { Duration } from '@mi-front/ui';
import { MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { MatchName } from '../components/audit-result/MatchName';
import { MatchStatuses } from '../components/audit-result/MatchStatuses';

export interface AuditMatchColumnOptions {
  /**
   * Type of time column to display
   * - 'time': Shows only time using LTime component
   * - 'datetime': Shows date and time using LDateTime component
   */
  timeColumnType?: 'time' | 'datetime';

  /**
   * Custom time/date column configuration
   * If provided, replaces the default time column
   * If null, time column is omitted
   */
  timeColumn?: MRT_ColumnDef<AuditMatch> | null;

  /**
   * Custom audio column configuration
   * If provided, replaces the default audio column
   */
  audioColumn?: Partial<MRT_ColumnDef<AuditMatch>> | null;

  /**
   * Custom status column configuration
   * If provided, replaces the default status column
   */
  statusColumn?: Partial<MRT_ColumnDef<AuditMatch>> | null;

  /**
   * Custom duration column configuration
   * If provided, replaces the default duration column
   */
  durationColumn?: Partial<MRT_ColumnDef<AuditMatch>> | null;

  /**
   * Custom actions column cell renderer
   * Component that receives the row and returns the actions UI
   * If null, actions column is omitted
   */
  actionsColumn?: ((row: MRT_Row<AuditMatch>) => ReactNode) | null;

  /**
   * Additional columns to include
   */
  extraColumns?: MRT_ColumnDef<AuditMatch>[];
}

/**
 * Hook that generates common column definitions for audit match tables.
 * Allows customization of individual columns while maintaining consistency.
 */
export function useAuditMatchColumns(
  options: AuditMatchColumnOptions = {},
): MRT_ColumnDef<AuditMatch>[] {
  const { $t } = useIntl();
  const {
    timeColumnType = 'time',
    timeColumn,
    audioColumn,
    statusColumn,
    durationColumn,
    actionsColumn,
    extraColumns = [],
  } = options;

  const columns: MRT_ColumnDef<AuditMatch>[] = [];

  // Time/Date column (included by default, customizable or can be omitted)
  if (timeColumn !== null) {
    // If custom timeColumn is provided, use it
    if (timeColumn) {
      columns.push(timeColumn);
    } else {
      // Otherwise, use default based on timeColumnType
      if (timeColumnType === 'time') {
        columns.push({
          id: 'time',
          header: $t({
            id: 'audit.props.time',
            defaultMessage: 'Hora',
          }),
          size: 110,
          Cell: ({ row: { original: match } }) => (
            <LTime value={match.datetime} second={'numeric'} />
          ),
        });
      } else if (timeColumnType === 'datetime') {
        columns.push({
          accessorKey: 'datetime',
          header: $t({
            id: 'audit.props.date',
            defaultMessage: 'Fecha',
          }),
          size: 100,
          Cell: ({ row }) => <LDateTime value={row.original.datetime} showRelative />,
        });
      }
    }
    // If timeColumn is explicitly null, no time column is added
  }

  // Audio column (always included by default, customizable)
  if (audioColumn !== null) {
    columns.push({
      accessorKey: 'audio.customId',
      header: $t({
        id: 'audit.props.audio',
        defaultMessage: 'Media',
      }),
      size: 120,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <div>
          <MatchName match={row.original} />
        </div>
      ),
      ...audioColumn,
    });
  }

  // Status column (with default implementation)
  if (statusColumn !== null) {
    columns.push({
      accessorKey: 'status',
      header: $t({
        id: 'common.status',
        defaultMessage: 'Estado',
      }),
      size: statusColumn?.size ?? 250,
      Cell: ({ row }) => <MatchStatuses match={row.original} />,
      ...statusColumn,
    });
  }

  // Duration column (always included by default, customizable)
  if (durationColumn !== null) {
    columns.push({
      accessorKey: 'audio.duration',
      header: $t({
        id: 'audit.props.duration',
        defaultMessage: 'Duración reproducida',
      }),
      size: 125,
      Cell: ({ row }) =>
        row.original.audio && (
          <Duration seconds={(row.original.audio.duration / 1000) as Seconds} />
        ),
      ...durationColumn,
    });
  }

  // Actions column (always included by default, customizable)
  if (actionsColumn !== null) {
    columns.push({
      id: 'actions',
      enableSorting: false,
      accessorFn: () => '',
      header: $t({
        id: 'common.text.actions',
        defaultMessage: 'Acciones',
      }),
      Cell: ({ row }) => (actionsColumn ? actionsColumn(row) : null),
    });
  }

  // Add any extra columns
  columns.push(...extraColumns);

  return columns;
}
